import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MSkeleton } from '@Components/Base';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { FormikHelpers, useFormik } from 'formik';

import { PresentationContainer, ViewResolver } from '@Components/index';
import { RequestList } from '@Components/RequestList';
import {
  useAppDispatch,
  useAppSelector,
  useIsLoadedConent,
  useRequestRefetcher,
  useRequestTypeFinderById,
  useStatusFinderByNum,
} from '@Hooks/index';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetApprovalHistoryMutation,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import {
  ApprovalHistory,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { HrmRequestFormResponse } from '@Types/api/form';
import { getModalStatus } from '@Store/selectors/modalSelector';
import { modalActions } from '@Store/slices/modalSlice';
import { NotificationContext } from '@Context/NotificationContext';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { FiltersWidget } from '@Components/FiltersWidget';
import { ViewWidget } from '@Components/ViewWidget';
import { SortConfig } from '@Features/types/SortConfig';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { SearchCriteria } from '@Types/api/common/filters';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import {
  getPassportStatusesDictSelector,
  getPassportTypeDictSelector,
} from '@Store/selectors/dictionarySelector';
import { isFiltersEmptySelector } from '@Store/selectors/filterOptionsSelector';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { mapColumns } from './list/MapColumns';
import { dataSourceMap } from './list/DataSource';
import Filters from './search/Filter';
import {
  filterTypes,
  getFiltersMapper,
  initialValuesFilterForm,
} from './search/filters.utils';
import { FilterForm } from './search/types';
import RequestViewForm from './form/View/RequestViewForm';
import RequestViewMobileForm from './form/View/mobile/RequestViewMobileForm';

type handleApplyFilterType = (
  values: FilterForm,
  formikHelpers: FormikHelpers<FilterForm>
) => void;

const PassportRequest: React.FC = () => {
  const {
    deleteSuccessNotification,
    errorNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isRequestFormPagePrev = useRef(isRequestFormPage);

  const isFiltersEmpty = useAppSelector(isFiltersEmptySelector);
  const purposeTypes = useAppSelector(getPassportTypeDictSelector);
  const statuses = useAppSelector(getPassportStatusesDictSelector);
  const isViewWidgetOpen = useAppSelector(getModalStatus);

  const { findStatus } = useStatusFinderByNum('Passport');
  const { findRequestType } = useRequestTypeFinderById('Passport');
  const [submitRequest] = useSubmitHrmRequestMutation();
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [cancelRequest] = useCancelHrmRequestMutation();
  const [getApprovalHistory] = useGetApprovalHistoryMutation();
  const { data: users = [] } = useGetUsersListByNameQuery(null);

  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    ApprovalHistory[] | null
  >(null);
  const [tagsValues, setTagsValues] = useState(initialValuesFilterForm);
  const [showTags, setShowTags] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const [sorting, setSorting] = useState<SortConfig | null>(null);

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: handleApplyFilterType = (options): void => {
    const filtersMapper = getFiltersMapper(purposeTypes, statuses);
    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );
    setFilters(searchCriterias);

    const tagOptions = parseSelectedFiltersToOptions(options);

    // true if filters was empty and empty after submit
    const isFiltersStillEmpty = isFiltersEmpty && !tagOptions.length;
    setShowFiltersError(isFiltersStillEmpty);

    if (!isFiltersStillEmpty) {
      closeFilters();
    }

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(tagOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);
  };

  const formik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(initialValuesFilterForm);
    formik.setValues(initialValuesFilterForm);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'Passport',
    searchValue,
    sorting,
  });
  const isContentLoaded = useIsLoadedConent(isSuccess);

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      fetchRequest(TABLE_PAGE_DEFAULT, sortConfig);

      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      setSorting(sortConfig);
    },
    [fetchRequest]
  );

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  const handleSubmitRequest = useCallback(
    (requestNo: string): void => {
      submitRequest(String(requestNo))
        .unwrap()
        .then(() => {
          submitSuccessNotification(String(requestNo));

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [fetchRequest]
  );

  const dataSource = useMemo(
    () =>
      dataSourceMap(
        listRequests as RequestListSearchResponse<HrmRequestFormResponse>,
        findStatus,
        findRequestType
      ),
    [listRequests, findStatus, findRequestType]
  );

  const handleChangePage = useCallback((page: number): void => {
    onChangeCurrentPage(page);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, []);

  const handleEditRecord = useCallback(
    (recordKey: string): void => {
      navigate(`${pathname}/edit/${recordKey}`);
    },
    [pathname]
  );

  const handleViewRecord = useCallback((recordKey: string): void => {
    dispatch(
      modalActions.setStatusModal({
        status: true,
        key: String(recordKey),
      })
    );
  }, []);

  const handleDeleteRequest = useCallback(
    (requestNo: string): void => {
      const reqId = String(requestNo);

      deleteRequest(reqId)
        .unwrap()
        .then(() => {
          deleteSuccessNotification(reqId);

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [fetchRequest]
  );

  const handleCloseViewWidget = (): void => {
    dispatch(modalActions.setStatusModal({ status: false, key: '' }));
  };

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
    dispatch(eventMessageActions.sendEventType('SEARCH'));
  }, []);

  const handleCancelRequest = useCallback(
    (requestNo: string) => {
      const reqId = String(requestNo);

      cancelRequest(reqId)
        .unwrap()
        .then(() => {
          cancelSuccessNotification(reqId);

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });

      handleCloseViewWidget();
    },
    [fetchRequest]
  );

  const handleApprovalHistory = useCallback((requestNo: string) => {
    getApprovalHistory(requestNo)
      .unwrap()
      .then((approvalHistoryResp) => {
        if (Array.isArray(approvalHistoryResp) && approvalHistoryResp.length) {
          setIsApprovalHistoryOpen(true);
          setApprovalHistoryData(approvalHistoryResp);
        }
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, []);

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  return (
    <PresentationContainer fullHeight={isRequestFormPage}>
      {isRequestFormPage ? (
        <Outlet />
      ) : (
        <>
          <FiltersWidget
            onSearchSubmit={handleSearch}
            isOpen={isFiltersOpen}
            onClose={closeFilters}
            onOpen={openFilters}
            formik={tagsFormik}
            showTags={showTags}
          >
            <Filters showError={showFiltersError} formik={formik} />
          </FiltersWidget>
          {isContentLoaded ? (
            <RequestList
              onLoadMore={handleLoadMore}
              onEdit={handleEditRecord}
              onView={handleViewRecord}
              onSubmit={handleSubmitRequest}
              onDelete={handleDeleteRequest}
              onCancel={handleCancelRequest}
              onApprovalHistory={handleApprovalHistory}
              onChangePage={handleChangePage}
              currentPage={currentPage}
              onSort={handleSort}
              requestsDataList={
                requestsDataList as Array<HrmRequestFormResponse>
              }
              listRequests={
                listRequests as RequestListSearchResponse<HrmRequestFormResponse>
              }
              mapColumns={mapColumns}
              dataSource={dataSource}
              requestType="Passport"
              isFiltersApplied={Boolean(filters)}
              isSearchApplied={Boolean(searchValue)}
            />
          ) : (
            <MSkeleton />
          )}
          <ViewWidget isOpen={isViewWidgetOpen} onClose={handleCloseViewWidget}>
            <ViewResolver
              desktop={<RequestViewForm />}
              mobile={
                <RequestViewMobileForm
                  onCancelRequest={handleCancelRequest}
                  onDeleteRequest={handleDeleteRequest}
                  onEdit={handleEditRecord}
                  onSubmitRequest={handleSubmitRequest}
                />
              }
            />
          </ViewWidget>
          <ApprovalHistoryModal
            isOpen={isApprovalHistoryOpen}
            onClose={() => {
              setIsApprovalHistoryOpen(false);
            }}
            data={approvalHistoryData}
            users={users}
          />
        </>
      )}
    </PresentationContainer>
  );
};

export default PassportRequest;
